import React, { useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import RestartAlt from '@mui/icons-material/RestartAlt'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import usePlannerAuthorization from '../generic/hooks/useAuthorization/usePlannerAuthorization'
import usePlanMetrics from './usePlanMetrics'
import LoadCapacityChart, { referenceYearOf, shortPeriodLabel } from './LoadCapacityChart'
import LoadCapacityGroupCards from './LoadCapacityGroupCards'
import LoadCapacitySummary from './LoadCapacitySummary'
import exportLoadCapacityCsv from './exportLoadCapacityCsv'
import { RANGE_OPTIONS, isSpecialBucket, summarizeBuckets, visibleBuckets } from './loadCapacityFormat'

// Story 177 (AP12), RN60 -- the two display preferences of the block live in the browser (`localStorage`), by
// browser and not by user (D-Q). Nothing saved (or anything but 'false') means visible; a browser that refuses
// to read or write it (private mode, blocked storage) must not show any warning (POL7): the buttons keep
// working while the user stays in the Planner.
const TOTALS_STORAGE_KEY = 'planner.loadCapacity.totalsVisible'
const LEGEND_STORAGE_KEY = 'planner.loadCapacity.legendVisible'

const readStoredVisibility = (key) => {
  try { return window.localStorage.getItem(key) !== 'false' } catch { return true }
}

const storeVisibility = (key, visible) => {
  try { window.localStorage.setItem(key, String(visible)) } catch { /* storage unavailable -- non-fatal */ }
}

// The pill look of the controls of the block (AP12, RN59), after the preview the PO validated.
const pillGroupSx = (theme) => ({
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '999px',
  padding: '2px',
  '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: '999px', padding: '2px 12px', border: 'none', color: 'text.secondary', fontSize: 13 },
  '& .MuiToggleButton-root.Mui-selected': {
    color: 'success.main',
    backgroundColor: alpha(theme.palette.success.main, 0.14),
    border: `1px solid ${alpha(theme.palette.success.main, 0.6)}`
  }
})

const pillTabsSx = (theme) => ({
  minHeight: 32,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '999px',
  padding: '2px',
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTab-root': { minHeight: 28, padding: '2px 12px', borderRadius: '999px', textTransform: 'none', fontSize: 13, color: 'text.secondary' },
  '& .MuiTab-root.Mui-selected': {
    color: 'success.main',
    backgroundColor: alpha(theme.palette.success.main, 0.14),
    border: `1px solid ${alpha(theme.palette.success.main, 0.6)}`
  }
})

// A label of the header that only informs (S208): it is not a control, so it has no arrow, no handler and no menu.
const InfoLabel = ({ label, value, testId }) => (
  <Stack
    direction='row'
    alignItems='center'
    spacing={0.75}
    sx={{ padding: '4px 12px', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
    data-testid={testId}
  >
    <Typography sx={{ fontSize: 10, letterSpacing: '0.12em', color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography variant='body2'>{value}</Typography>
  </Stack>
)

// I2/RN5 -- the same five global filters the ribbon forwards (S84): `reading`, `grouping`,
// `deviceGroupId`, `ungrouped` and `deviceId` are this block's own, local parameters, never read from
// `plannerFilters`.
const GLOBAL_PARAM_KEYS = ['fabricationOrderId', 'partReferenceId', 'orderId', 'clientId', 'timeZone']

const toGlobalParams = (plannerFilters = {}) => GLOBAL_PARAM_KEYS.reduce((params, key) => {
  if (plannerFilters[key] !== null && plannerFilters[key] !== undefined) {
    params[key] = plannerFilters[key]
  }
  return params
}, {})

// V8/E1-E6 -- Sun's stable `code`s that this block has a dedicated literal for (S97); anything else
// falls back to the generic one, same idiom as `PlanIndicatorsRibbon`/`PlannerEventDialog`.
const ERROR_MESSAGE_KEY_BY_CODE = {
  PLAN_NOT_FOUND: 'Planification not found',
  DEVICE_GROUP_NOT_FOUND: 'Device group not found or inactive',
  DEVICE_NOT_ELIGIBLE: 'Device not found, inactive or forecast',
  HORIZON_TOO_LONG: 'Plan horizon exceeds the 104 week limit'
}

const ALL_RANGE = 'ALL'

// Above this many machines the list under the chart title is folded; the planner unfolds it.
const FOLDED_DEVICE_NAMES = 4

const LEGEND = [
  { key: 'firm', label: 'Firm load', color: 'success.main' },
  { key: 'forecast', label: 'Forecast load', color: 'warning.main' },
  { key: 'available', label: 'Available', color: 'action.hover', bordered: true },
  { key: 'oee-loss', label: 'OEE loss', hatched: true },
  { key: 'capacity', label: 'OEE capacity', line: true },
  { key: 'excess', label: 'Excess over capacity', outlined: true }
]

const LegendSwatch = ({ item }) => (
  <Box
    component='span'
    sx={(theme) => ({
      display: 'inline-block',
      width: item.line ? 16 : 12,
      height: item.line ? 2 : 12,
      borderRadius: item.line ? 0 : 0.5,
      backgroundColor: item.line
        ? theme.palette.text.primary
        : item.hatched
          ? `repeating-linear-gradient(45deg, ${theme.palette.text.disabled} 0 1.5px, transparent 1.5px 5px)`
          : (item.outlined ? 'transparent' : undefined),
      background: item.hatched ? `repeating-linear-gradient(45deg, ${theme.palette.text.disabled} 0 1.5px, transparent 1.5px 5px)` : undefined,
      bgcolor: !item.line && !item.hatched && !item.outlined ? item.color : undefined,
      border: item.outlined
        ? `1.5px solid ${theme.palette.error.main}`
        : (item.bordered ? `1px dashed ${theme.palette.divider}` : 'none'),
      verticalAlign: 'middle',
      marginRight: '0.4rem'
    })}
  />
)

/**
 * CU8 -- the "Load and capacity" accordion content: the group cards (which also select the group),
 * a header with the scope and the controls (Planned date/Delivery date, Week/Month, how many periods
 * to show, whether to draw the OEE loss), a strip of totals over the shown periods, the chart and its
 * legend. Jupiter computes no business figure here (RN44): it only reads `planMetrics/{id}/loadCapacity`
 * and paints; the strip merely adds up the per-column numbers the server sent.
 * RN55 (no call while collapsed) needs no code of its own: `DefaultAccordion` unmounts this component
 * while collapsed, so its hooks simply never run (see anchors §2).
 */
const LoadCapacityBlock = ({ planificationId, plannerFilters = {}, metricsVersion = 0 }) => {
  const { t_ } = useSmartTranslation()
  const isUserPlannerAuthorized = usePlannerAuthorization()
  const isAuthorized = isUserPlannerAuthorized('PLANNER')
  const hasSelectedPlan = planificationId !== null && planificationId !== undefined

  const [reading, setReading] = useState('PLANNED_DATE')
  const [grouping, setGrouping] = useState('WEEK')
  const [range, setRange] = useState(RANGE_OPTIONS.WEEK[0])
  const [showOeeLoss, setShowOeeLoss] = useState(true)
  const [groupSelection, setGroupSelection] = useState({ deviceGroupId: null, ungrouped: false })
  // The "All groups" card keeps describing the whole plant while a group is selected: it remembers
  // the scope of the last unfiltered answer, since a filtered one only describes its group.
  const [allGroupsSummary, setAllGroupsSummary] = useState(null)
  const [totalsVisible, setTotalsVisible] = useState(() => readStoredVisibility(TOTALS_STORAGE_KEY))
  const [legendVisible, setLegendVisible] = useState(() => readStoredVisibility(LEGEND_STORAGE_KEY))
  const [allDevicesShown, setAllDevicesShown] = useState(false)

  const params = {
    ...toGlobalParams(plannerFilters),
    reading,
    grouping,
    // RN4/S84 -- `deviceGroupId` and `ungrouped` never travel together, and `ungrouped` never travels
    // as `false`: absent means "all groups" (RN4), so only `true` is ever sent.
    deviceGroupId: groupSelection.ungrouped ? undefined : (groupSelection.deviceGroupId ?? undefined),
    ungrouped: groupSelection.ungrouped ? true : undefined
  }

  const { data, error, reload } = usePlanMetrics({
    planificationId,
    endpoint: 'loadCapacity',
    params,
    enabled: isAuthorized && hasSelectedPlan
  })

  // CU9/S100-S101/S104: same recalculation trigger as `PlanIndicatorsRibbon` (see there for the full
  // rationale). RN55 needs no extra guard here: while this block is collapsed, `DefaultAccordion`
  // unmounts it (anchors §2), so this effect -- like every other hook in this component -- simply
  // does not run, and no `metricsVersion` bump reaches it (S103).
  const isFirstMetricsVersionRender = useRef(true)
  useEffect(() => {
    if (isFirstMetricsVersionRender.current) {
      isFirstMetricsVersionRender.current = false
      return
    }
    reload()
  }, [metricsVersion, reload])

  useEffect(() => {
    if (!data || data.scope.deviceGroupId !== null || data.scope.ungrouped) {
      return
    }
    // S122/RN29-G3 (GATE 3, F9) -- the weekly figure of the whole plant is `scope.weeklyCapacityHours`,
    // the server's own RN29 over the machines in scope; nothing is averaged or added up here (RN44).
    setAllGroupsSummary({ deviceCount: data.scope.deviceCount, weeklyCapacityHours: data.scope.weeklyCapacityHours })
  }, [data])

  const shownBuckets = useMemo(() => visibleBuckets(data ? data.buckets : [], range === ALL_RANGE ? null : range), [data, range])
  const summary = useMemo(() => summarizeBuckets(shownBuckets), [shownBuckets])

  const changeGrouping = (newGrouping) => {
    if (!newGrouping || newGrouping === grouping) {
      return
    }
    setGrouping(newGrouping)
    setRange(RANGE_OPTIONS[newGrouping][0])
  }

  // S191/D-N.2 -- "Reset" gives back only the controls of this block to their first state. The global filters of
  // the Timeline (`plannerFilters`) and the "Totals"/"Legend" preferences are not the block's to reset. Nothing
  // is requested here: the request follows from the controls, as it does when the user changes them by hand.
  const resetControls = () => {
    setReading('PLANNED_DATE')
    setGrouping('WEEK')
    setRange(RANGE_OPTIONS.WEEK[0])
    setShowOeeLoss(true)
    setGroupSelection({ deviceGroupId: null, ungrouped: false })
  }

  const toggleTotals = () => {
    const next = !totalsVisible
    setTotalsVisible(next)
    storeVisibility(TOTALS_STORAGE_KEY, next)
  }

  const toggleLegend = () => {
    const next = !legendVisible
    setLegendVisible(next)
    storeVisibility(LEGEND_STORAGE_KEY, next)
  }

  // P2/A5/S96 -- no permission or no selected plan: nothing is requested, nothing is painted.
  if (!isAuthorized || !hasSelectedPlan) {
    return null
  }

  // P3/S97 -- a failed calculation never takes the Gantt (or the rest of the accordion) down with it.
  if (error) {
    const specificMessageKey = ERROR_MESSAGE_KEY_BY_CODE[error.data?.code]
    return (
      <Alert
        severity='error'
        action={<Button color='inherit' size='small' onClick={reload}>{t_('Retry')}</Button>}
      >
        <Typography variant='body2'>
          {specificMessageKey ? t_(specificMessageKey) : t_('Plan metrics could not be loaded')}
        </Typography>
      </Alert>
    )
  }

  if (!data) {
    return null
  }

  const scope = data.scope
  const regularShown = shownBuckets.filter((bucket) => !isSpecialBucket(bucket))
  // The bounds of the shown axis, abbreviated exactly as the axis labels are (S39–S51, 2026-09–2026-12).
  const referenceYear = referenceYearOf(regularShown)
  const axisFrom = regularShown.length > 0 ? shortPeriodLabel(regularShown[0], referenceYear, t_) : scope.horizonStartPeriodKey
  const axisTo = regularShown.length > 0 ? shortPeriodLabel(regularShown[regularShown.length - 1], referenceYear, t_) : scope.horizonEndPeriodKey
  const scopeTitle = scope.ungrouped ? t_('No group') : (scope.deviceGroupName ?? t_('All groups'))
  // H153 S92 (amended) -- the short scope line; the planned-date one is today's key, the delivery-date one is new.
  const scopeHeaderText = data.reading === 'DELIVERY_DATE'
    ? t_('{{count}} machines · by delivery date · {{from}}–{{to}}', { count: scope.deviceCount, from: axisFrom, to: axisTo })
    : t_('{{count}} machines · by planned date · {{from}}–{{to}}', { count: scope.deviceCount, from: axisFrom, to: axisTo })
  const rangeUnitKey = grouping === 'MONTH' ? '{{count}} mo' : '{{count}} wk'
  const legendItems = LEGEND.filter((item) => item.key !== 'oee-loss' || showOeeLoss)
  // S208 -- informative labels only: the plan the planner is looking at and the group of the load.
  const groupLabelValue = scope.ungrouped ? t_('No group') : (scope.deviceGroupName ?? t_('All'))

  return (
    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }} data-testid='load-capacity-block'>
      {/* The accordion already says "Load and capacity": the PO dropped the block's own title and explanation. */}
      <Stack direction='row' alignItems='flex-start' justifyContent='flex-end' flexWrap='wrap' useFlexGap spacing={2} data-testid='load-capacity-block-header'>
        <Stack direction='row' alignItems='center' spacing={1.25} flexWrap='wrap' useFlexGap>
          <InfoLabel label={t_('Planning')} value={t_('{{plan}} · active', { plan: data.planificationName ?? '' })} testId='load-capacity-plan-label' />
          <InfoLabel label={t_('Group')} value={groupLabelValue} testId='load-capacity-group-label' />
          <Button
            size='small'
            variant='outlined'
            color='inherit'
            startIcon={<RestartAlt />}
            onClick={resetControls}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            data-testid='load-capacity-reset'
          >
            {t_('Reset')}
          </Button>
          <Button
            size='small'
            variant='outlined'
            color='inherit'
            startIcon={<FileDownloadOutlined />}
            onClick={() => exportLoadCapacityCsv({ buckets: shownBuckets, t_, filename: t_('Load and capacity') })}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            data-testid='load-capacity-export'
          >
            {t_('Export')}
          </Button>
        </Stack>
      </Stack>

      <LoadCapacityGroupCards
        groups={data.groups}
        deviceGroupId={groupSelection.deviceGroupId}
        ungrouped={groupSelection.ungrouped}
        onChange={setGroupSelection}
        allGroupsSummary={allGroupsSummary}
        grouping={data.grouping}
      />

      {/* S199 -- no `overflow: hidden` here: the chart fits the block and nothing of it may be cut off. */}
      <Paper variant='outlined' sx={{ borderRadius: 3, minWidth: 0, maxWidth: '100%' }}>
        <Stack spacing={2} sx={{ padding: '1rem 1.25rem' }}>
          <Stack direction='row' spacing={2} alignItems='flex-start' justifyContent='space-between' flexWrap='wrap' useFlexGap>
            <Stack data-testid='load-capacity-scope-header' sx={{ minWidth: '16rem', flex: '1 1 20rem' }}>
              <Typography sx={{ fontWeight: 700 }} data-testid='load-capacity-scope-title'>
                {t_('Planned load against capacity · {{group}}', { group: scopeTitle })}
              </Typography>
              <Typography variant='caption' color='text.secondary' data-testid='load-capacity-scope-summary'>
                {scopeHeaderText}
              </Typography>
              {scope.deviceNames.length > 0
                ? (
                  <Typography variant='caption' color='text.disabled' component='div' data-testid='load-capacity-scope-devices'>
                    {/* More than four machines: folded by default, the planner unfolds the rest. */}
                    {allDevicesShown || scope.deviceNames.length <= FOLDED_DEVICE_NAMES
                      ? scope.deviceNames.join(', ')
                      : scope.deviceNames.slice(0, FOLDED_DEVICE_NAMES).join(', ')}
                    {scope.deviceNames.length > FOLDED_DEVICE_NAMES
                      ? (
                        <Link
                          component='button'
                          type='button'
                          variant='caption'
                          underline='hover'
                          onClick={() => setAllDevicesShown((shown) => !shown)}
                          sx={{ marginLeft: '0.5rem', verticalAlign: 'baseline' }}
                          data-testid='load-capacity-scope-devices-toggle'
                        >
                          {allDevicesShown
                            ? t_('Show less')
                            : t_('+{{count}} more', { count: scope.deviceNames.length - FOLDED_DEVICE_NAMES })}
                        </Link>
                        )
                      : null}
                  </Typography>
                  )
                : null}
              {scope.devicesWithoutCalendarCount > 0
                ? (
                  <Typography variant='body2' color='warning.main' data-testid='load-capacity-no-calendar-warning'>
                    {t_('{{count}} machines without calendar', { count: scope.devicesWithoutCalendarCount })}
                  </Typography>
                  )
                : null}
              {/* S117 -- only in the delivery-date reading, and only when the count is above zero; the
                  figure is read as-is from `scope`, never recounted from `buckets` (RN44). */}
              {data.reading === 'DELIVERY_DATE' && scope.fabricationOrdersWithoutTargetDate > 0
                ? (
                  <Typography variant='body2' color='warning.main' data-testid='load-capacity-without-target-date-warning'>
                    {t_('{{count}} FOs without required date', { count: scope.fabricationOrdersWithoutTargetDate })}
                  </Typography>
                  )
                : null}
            </Stack>

            <Stack direction='row' spacing={1.25} alignItems='center' flexWrap='wrap' useFlexGap data-testid='load-capacity-controls'>
              <Tabs
                value={reading}
                onChange={(event, newReading) => setReading(newReading)}
                data-testid='load-capacity-reading-tabs'
                sx={pillTabsSx}
              >
                <Tab label={t_('Planned date')} value='PLANNED_DATE' />
                <Tab label={t_('Delivery date')} value='DELIVERY_DATE' />
              </Tabs>
              <ToggleButtonGroup
                exclusive
                value={grouping}
                onChange={(event, newGrouping) => changeGrouping(newGrouping)}
                size='small'
                sx={pillGroupSx}
                data-testid='load-capacity-grouping-toggle'
              >
                <ToggleButton value='WEEK'>{t_('Week')}</ToggleButton>
                <ToggleButton value='MONTH'>{t_('Month')}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                exclusive
                value={range}
                onChange={(event, newRange) => { if (newRange !== null) { setRange(newRange) } }}
                size='small'
                sx={pillGroupSx}
                data-testid='load-capacity-range-toggle'
              >
                {RANGE_OPTIONS[grouping].map((option) => (
                  <ToggleButton key={option} value={option} data-testid={`load-capacity-range-${option}`}>
                    {t_(rangeUnitKey, { count: option })}
                  </ToggleButton>
                ))}
                <ToggleButton value={ALL_RANGE} data-testid='load-capacity-range-all'>{t_('All')}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup size='small' sx={pillGroupSx} value={showOeeLoss ? ['oee-loss'] : []} onChange={() => setShowOeeLoss((shown) => !shown)}>
                <ToggleButton value='oee-loss' data-testid='load-capacity-oee-loss-toggle'>{t_('OEE loss')}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup size='small' sx={pillGroupSx} value={totalsVisible ? ['totals'] : []} onChange={toggleTotals}>
                <ToggleButton value='totals' data-testid='load-capacity-totals-toggle'>{t_('Totals')}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup size='small' sx={pillGroupSx} value={legendVisible ? ['legend'] : []} onChange={toggleLegend}>
                <ToggleButton value='legend' data-testid='load-capacity-legend-toggle'>{t_('Legend')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {totalsVisible ? <LoadCapacitySummary summary={summary} showOeeLoss={showOeeLoss} /> : null}

          <LoadCapacityChart buckets={shownBuckets} showOeeLoss={showOeeLoss} variant='block' />

          {legendVisible
            ? (
              <Stack direction='row' spacing={2.5} flexWrap='wrap' useFlexGap data-testid='load-capacity-legend'>
                {legendItems.map((item) => (
                  <Typography key={item.key} variant='caption' color='text.secondary' component='span' sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <LegendSwatch item={item} />
                    {t_(item.label)}
                  </Typography>
                ))}
              </Stack>
              )
            : null}
        </Stack>
      </Paper>
    </Stack>
  )
}

export default LoadCapacityBlock
